import { ReactNode } from 'react'
import { documentToReactComponents, Options, RenderNode, RenderText } from '@contentful/rich-text-react-renderer'
import { BLOCKS, INLINES, Document } from '@contentful/rich-text-types'
import classNames from 'classnames'
import { Asset } from 'contentful'
import { ParagraphMD, TitleLG, TitleMD, TitleSM } from '@/atoms/Text'
import { VideoBlock } from '@/atoms/VideoBlock'
import ContentfulImage from '@/molecules/ContentfulImage'
import { options as defaultOptions } from '@/services/ContentfulService'
import { IVideoBlockFields } from '@/types/generated-contentful'

export interface Props {
  className?: string
  document: Document
  optionOverrides?: RenderNode
  textReplacement?: RenderText
}

export const getOptions: (overrides?: RenderNode, renderText?: RenderText) => Options = (overrides, renderText) => ({
  renderNode: {
    ...defaultOptions.renderNode,
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const content = node.data.target as Asset
      const contentType = content.fields?.file?.contentType || ''

      if (content.fields && contentType.match(/^image/)) {
        return (
          <div className="mb-8 w-full last:mb-0">
            <ContentfulImage image={content} className="w-full" quality={100} width={975} lazy />
          </div>
        )
      }

      return null
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const { fields, sys } = node.data.target || {}
      const contentType = sys?.contentType?.sys?.id || ''

      if (contentType === 'videoBlock') {
        const videoBlock = fields as IVideoBlockFields
        return <VideoBlock {...videoBlock} className="mb-8 last:mb-0" />
      }

      return null
    },
    // h2-h4 are the only supported headings in Contentful at this time
    [BLOCKS.HEADING_2]: (_, children) => (
      <TitleLG weight="semibold" className="mb-4">
        {children}
      </TitleLG>
    ),
    [BLOCKS.HEADING_3]: (_, children) => (
      <TitleMD as={(props) => <h3 {...props} />} weight="semibold" className="mb-4">
        {children}
      </TitleMD>
    ),
    [BLOCKS.HEADING_4]: (_, children) => (
      <TitleSM as={(props) => <h4 {...props} />} weight="semibold" className="mb-4">
        {children}
      </TitleSM>
    ),
    [BLOCKS.OL_LIST]: (node) => {
      const messages = node.content.map((listItem) =>
        // render the inner <p> as the message
        // @ts-expect-error unknown type
        documentToReactComponents(listItem.content[0], {
          renderNode: {
            ...defaultOptions.renderNode,
            [BLOCKS.PARAGRAPH]: (node, children) => {
              return (
                <ParagraphMD color="core-gray-950" className="mb-4 last:mb-0">
                  {children}
                </ParagraphMD>
              )
            },
          },
        }),
      )
      return (
        <ol className="m-3 mb-8 [counter-reset:li] md:m-0">
          {messages.map((message: ReactNode, index: number) => (
            <li
              key={`highlight-${index}`}
              className={classNames(
                'flex flex-row items-center mb-5',
                'before:items-center before:bg-white before:rounded-full before:shadow before:shadow-gray-600 before:content-[counter(li)] before:[counter-increment:li] before:flex before:font-whitney before:text-lg before:h-5 before:justify-center before:mr-3 before:p-5 before:w-5',
              )}
            >
              {message}
            </li>
          ))}
        </ol>
      )
    },
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <ParagraphMD color="core-gray-950" className="mb-4 last:mb-0">
        {children}
      </ParagraphMD>
    ),
    [BLOCKS.QUOTE]: (_, children) => {
      return (
        <blockquote className="mb-8 border-l-[5px] border-solid border-l-core-gray-200 pl-6">{children}</blockquote>
      )
    },
    [BLOCKS.UL_LIST]: (_, children) => {
      return <ul className="my-8 ml-10 mr-0 list-disc">{children}</ul>
    },
    [INLINES.HYPERLINK]: ({ data }, children) => {
      return (
        <a className="text-core-oxide hover:text-core-oxide-bright" target="_blank" rel="noreferrer" href={data.uri}>
          {children}
        </a>
      )
    },
    ...overrides,
  },
  renderText,
})

const TextBlock = ({ className, document, optionOverrides, textReplacement }: Props) => {
  return (
    <div className={className}>{documentToReactComponents(document, getOptions(optionOverrides, textReplacement))}</div>
  )
}

export default TextBlock
